/**
 * RepoGraphEmbed — grafo de conhecimento (Graphify) de UM projeto, embutido.
 *
 * Fonte única do "grafo do repositório": usado tanto na tela global
 * (GraphViewScreen, com seletor de projeto) quanto DENTRO da Jornada
 * (passo "Análise do Repositório"), onde o projeto já é o da jornada.
 *
 * O grafo é construído automaticamente ao indexar o GitHub do projeto e servido
 * em /graphs/<project_id>/graph.html (graphify-viz). Base: VITE_GRAPHIFY_VIZ_URL.
 */
import { useState, useMemo, useEffect } from 'react';
import { RefreshCw, ExternalLink } from 'lucide-react';
import { C } from '@/theme/palette';
import { Btn } from '@/components/ui';
import { useGitRepositories } from '@/api/git';

/** Nome curto do repo (último segmento de owner/repo) para rótulo do seletor. */
function repoShortName(fullName) {
  if (!fullName) return 'repo';
  const parts = String(fullName).split('/');
  return parts[parts.length - 1] || fullName;
}

/**
 * Base do graphify-viz. Prioridade:
 *   1. VITE_GRAPHIFY_VIZ_URL (se definido no build) — override explícito.
 *   2. `/graphs` (default) — caminho RELATIVO na mesma origem do front. O nginx
 *      faz reverse-proxy /graphs/ → graphify-viz:8091 (ver app-common.conf).
 *      Assim o grafo é servido pela mesma porta já exposta (3000/443), sem
 *      exigir que a porta 8091 esteja aberta externamente — funciona tanto em
 *      localhost quanto por IP pública, e sem mixed-content em HTTPS.
 */
export const GRAPHIFY_VIZ_BASE =
  (import.meta.env.VITE_GRAPHIFY_VIZ_URL ?? '/graphs').replace(/\/+$/, '');

/**
 * @param {string}  projectId  Projeto cujo grafo será exibido.
 * @param {boolean} fill       true = preenche o container (flex:1, altura 100%);
 *                             false (default) = altura fixa `minHeight`.
 * @param {number}  minHeight  Altura da viz quando não é `fill` (px).
 */
export function RepoGraphEmbed({ projectId, fill = false, minHeight = 560 }) {
  const [nonce, setNonce] = useState(0);

  // Multi-repo: um projeto pode ter N repositórios indexados. O seletor alterna
  // entre o grafo UNIFICADO do projeto (`/graphs/<pid>/graph.html`, todos os repos
  // mesclados) e o grafo SEPARADO de cada repo (`/graphs/<pid>/<repo_id>/graph.html`).
  const { data: reposData } = useGitRepositories(projectId);
  const repos = useMemo(() => {
    const list = Array.isArray(reposData) ? reposData : (reposData?.items ?? []);
    // Só repos com grafo pronto (indexação concluída) entram como opção separada.
    return list.filter((r) => r?.knowledge_pack_status === 'ready' || (r?.indexed_docs ?? 0) > 0);
  }, [reposData]);

  // '' = unificado (todos). Caso contrário, o repo_id selecionado.
  const [selectedRepo, setSelectedRepo] = useState('');
  // Reseta a seleção quando troca de projeto.
  useEffect(() => { setSelectedRepo(''); }, [projectId]);

  const src = useMemo(() => {
    if (!projectId) return '';
    const suffix = selectedRepo ? `${selectedRepo}/graph.html` : 'graph.html';
    return `${GRAPHIFY_VIZ_BASE}/${projectId}/${suffix}?v=${nonce}`;
  }, [projectId, selectedRepo, nonce]);

  // Disponibilidade do graph.html. O graphify NÃO gera a visualização HTML acima
  // de GRAPHIFY_VIZ_NODE_LIMIT nós (grafo muito grande) — nesse caso o iframe
  // ficaria em BRANCO sem explicação. Fazemos um HEAD para detectar (404) e mostrar
  // uma mensagem clara em vez do iframe vazio. null = verificando; true/false = resultado.
  const [available, setAvailable] = useState(null);
  useEffect(() => {
    if (!projectId) { setAvailable(null); return; }
    let alive = true;
    setAvailable(null);
    const suffix = selectedRepo ? `${selectedRepo}/graph.html` : 'graph.html';
    fetch(`${GRAPHIFY_VIZ_BASE}/${projectId}/${suffix}?v=${nonce}`, { method: 'HEAD' })
      .then((r) => { if (alive) setAvailable(r.ok); })
      .catch(() => { if (alive) setAvailable(false); });
    return () => { alive = false; };
  }, [projectId, selectedRepo, nonce]);

  const wrapperStyle = fill
    ? { display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }
    : { display: 'flex', flexDirection: 'column' };

  const vizStyle = fill
    ? { position: 'relative', flex: 1, minHeight: 0, backgroundColor: C.black }
    : { position: 'relative', height: minHeight, backgroundColor: C.black,
        borderRadius: 8, overflow: 'hidden', border: `1px solid ${C.blackBorder}` };

  return (
    <div style={wrapperStyle}>
      {/* Barra de ações — só quando há projeto */}
      {projectId && (
        <div
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
            gap: 8, marginBottom: 10,
          }}
        >
          {/* Seletor de repo: Todos (unificado) + cada repo indexado. Só quando há
              mais de um repo — com um único repo, unificado == esse repo. */}
          {repos.length > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginRight: 'auto' }}>
              <span style={{ fontSize: 12, color: C.neutralDim }}>Repositório:</span>
              <select
                value={selectedRepo}
                onChange={(e) => setSelectedRepo(e.target.value)}
                title="Escolha o grafo unificado do projeto ou o de um repositório específico"
                style={{
                  padding: '6px 9px', backgroundColor: C.blackElev, color: C.neutral,
                  border: `1px solid ${C.blackBorder}`, borderRadius: 6, fontSize: 12.5, minWidth: 190,
                }}
              >
                <option value="">Todos (unificado) — {repos.length} repos</option>
                {repos.map((r) => (
                  <option key={r.id} value={r.id}>
                    {repoShortName(r.full_name)}{typeof r.indexed_docs === 'number' ? ` (${r.indexed_docs})` : ''}
                  </option>
                ))}
              </select>
            </div>
          )}
          <Btn variant="ghost" size="sm" icon={RefreshCw} onClick={() => setNonce((n) => n + 1)}>
            Atualizar
          </Btn>
          <Btn
            variant="outline" size="sm" icon={ExternalLink}
            onClick={() => src && window.open(src, '_blank', 'noopener')}
          >
            Nova aba
          </Btn>
        </div>
      )}

      {/* Viz embutida / estado vazio */}
      <div style={vizStyle}>
        {projectId && available === false ? (
          <div style={{ padding: 40, color: C.neutralDim, maxWidth: 640 }}>
            <p style={{ fontSize: 14, marginBottom: 8, color: C.neutral }}>
              A visualização interativa não está disponível para este repositório.
            </p>
            <p style={{ fontSize: 12.5, color: C.neutralMuted, lineHeight: 1.5 }}>
              O grafo de código foi construído e é usado pelo Assistente e pela análise
              (GraphRAG), mas ainda não foi renderizado como HTML — normalmente porque o
              grafo é grande demais para a visualização embutida (acima do limite de nós).
              Você ainda pode reindexar o repositório ou aumentar
              <b style={{ color: C.neutralDim }}> GRAPHIFY_VIZ_NODE_LIMIT</b>.
            </p>
          </div>
        ) : projectId ? (
          <iframe
            key={`${projectId}-${nonce}`}
            title="Grafo do Repositório (Graphify)"
            src={src}
            style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
            sandbox="allow-scripts allow-same-origin allow-popups"
          />
        ) : (
          <div style={{ padding: 40, color: C.neutralDim, maxWidth: 640 }}>
            <p style={{ fontSize: 14, marginBottom: 8 }}>
              Nenhum grafo disponível ainda para este projeto.
            </p>
            <p style={{ fontSize: 12.5, color: C.neutralMuted }}>
              O grafo é construído automaticamente quando você conecta e indexa o repositório
              do GitHub do projeto (etapa <b style={{ color: C.neutralDim }}>Repositório Git</b> da Jornada).
              Volte aqui após a indexação para revisar a análise.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
